import { takeEvery, all, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { SweetAlertResult } from 'sweetalert2';
import moment from 'moment';
import apiService from 'app/bff/services/apiService';
import { MethodTypesEnum } from 'app/bff/enums/user/methodTypesEnum';
import { AaOtpSendcodeResp } from 'app/bff/models/aa/aaOtpSendcode';
import { AaOtpVerifyResp } from 'app/bff/models/aa/aaOtpVerify';
import { MbUpdateinfoReq, MbUpdateinfoResp } from 'app/bff/models/mb/mbUpdateinfo';
import { ROUTES } from 'app/core/router/paths';
import modalService from 'app/core/services/modalService';
import { ProcessRouteMatchesStep } from 'app/features/Member/Register/Permission/types';
import { FormValues as ConfirmInfoFormValues } from 'app/features/Member/Register/Permission/ConfirmInfo/types';
import { RootState } from 'app/store/types';
import { sagaBoundary } from 'app/store/service';
import { MEMBER_REGISTER_PERMISSION__SEND_OTP, MEMBER_REGISTER_PERMISSION__RESEND_OTP, MEMBER_REGISTER_PERMISSION__AUTH_OTP, MEMBER_REGISTER_PERMISSION__SEND_REGISTER_DATA, SendOTPAction, ResendOTPAction, AuthOTPAction, SendRegisterDataAction } from './types';
import { sendOTPDoneAction, setAccessibleStepAction, resendOTPDoneAction, sendRegisterDataAction } from './actions';

/**
 * @description 發送 OTP 驗證碼
 */
function * sendOTP (action: SendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) {
    // 緩存 OTP 資料
    yield put(sendOTPDoneAction(response));
    // 將「確認資料」(Step-2-2) 設為可訪問步驟
    yield put(setAccessibleStepAction(ProcessRouteMatchesStep[ROUTES.MEMBER__REGISTER__PERMISSION__CONFIRM_INFO__OTP_AUTH]));
    // 導頁至「確認資料」(Step-2-2)
    yield put(push(ROUTES.MEMBER__REGISTER__PERMISSION__CONFIRM_INFO__OTP_AUTH));
  }
}

/**
 * @description 重新發送 OTP 驗證碼
 */
function * resendOTP (action: ResendOTPAction) {
  const response: AaOtpSendcodeResp = yield call(apiService.postAaOtpSendcode, action.payload.args);
  if (response) yield put(resendOTPDoneAction(response));
}

/**
 * @description 驗證 OTP 驗證碼
 */
function * authOTP (action: AuthOTPAction) {
  const response: AaOtpVerifyResp = yield call(apiService.postAaOtpVerify, action.payload.authOTPArgs);
  if (response) {
    // 送出會員資料執行註冊 (更新會員資料)
    if (action.payload.registerArgs.method === MethodTypesEnum.AOI) {
      const args: MbUpdateinfoReq = {
        ...action.payload.registerArgs,
        users: {
          ...action.payload.registerArgs.users,
          regOtpTime: moment().format('YYYY-MM-DD HH:mm:ss')
        }
      };
      yield put(sendRegisterDataAction(args));
    }
  }
}

/**
 * @description 送出會員資料執行註冊
 */
function * sendRegisterData (action: SendRegisterDataAction) {
  const response: MbUpdateinfoResp = yield call(apiService.postMbUpdateinfo, action.payload.args);
  if (response) {
    const confirmInfoPageState: ConfirmInfoFormValues = yield select((state: RootState) => state.features.member.register.permission.process.confirmInfo.data);
    if (!confirmInfoPageState) return;
    const result: SweetAlertResult = yield modalService.confirm(
      '啟用成功',
      `<p class="m-0 w-100 text-start">感謝您加入臺銀人壽網路會員！ 提醒您，為維護您的權益，若您於 5 年之間未與本公司進行網路投保等事宜者，本公司將重新驗證您的身分。</p>
       <p class="m-0 text-primary w-100 text-start">*您的推薦碼：${confirmInfoPageState.referralCode}（可於會員中心查詢）</p>`,
      '立即登入',
      '回首頁'
    );
    if (result.value) {
      // 開啟會員登入 Modal
      modalService.login(true);
    }
    yield put(push(ROUTES.HOME__MAIN));
  }
}

export default function * watchPermissionSaga () {
  yield all([
    takeEvery(MEMBER_REGISTER_PERMISSION__SEND_OTP, sagaBoundary(sendOTP)),
    takeEvery(MEMBER_REGISTER_PERMISSION__RESEND_OTP, sagaBoundary(resendOTP)),
    takeEvery(MEMBER_REGISTER_PERMISSION__AUTH_OTP, sagaBoundary(authOTP)),
    takeEvery(MEMBER_REGISTER_PERMISSION__SEND_REGISTER_DATA, sagaBoundary(sendRegisterData))
  ]);
}
